import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import startCase from 'lodash/startCase';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import {
  CustomPlanTemplatesDocument,
  CustomPlanTemplatesFilter,
  MemberDocument,
  PerformanceQualityAttributesDocument,
  Plan,
  PlanTrainingMode,
  UserPlanBasicsFragment,
} from '../../../../graphql/types';
import { SplitFlags } from '../../../../hooks/featureFlags';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import {
  EventCategories,
  EventLabels,
  EventNames,
} from '../../../../utils/trackingEvents';
import LoadingPanel from '../../../LoadingPanel';
import Button from '../../../reusable/Button';
import GenericError from '../../../reusable/GenericError';
import { FeatureHeader } from '../../../reusable/Header/FeatureHeader';
import { Link } from '../../../reusable/Link';
import LoadMore from '../../../reusable/LoadMore';
import PaginationWaypoint from '../../../reusable/PaginationWaypoint';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '../../../reusable/Table';
import Text, { TextColor, TextKind } from '../../../reusable/Text';
import { GameplanTemplateFilters } from './GameplanTemplateFilters';

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 128px;
`;

const StyledPage = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding:  24px;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

interface TemplateItemProps {
  template: UserPlanBasicsFragment;
}

export function getTrainingModeText(trainingMode?: PlanTrainingMode | null) {
  switch (trainingMode) {
    case PlanTrainingMode.TrainingPlan:
      return 'MxM Training Plan';
    case PlanTrainingMode.Classes:
      return 'On-Demand Training Classes';
    default:
      return 'None';
  }
}

export function getPhase(phase: Plan['phase']): string {
  if (typeof phase === 'number') {
    return `Phase ${phase}`;
  }

  return 'Phase 1';
}

const TemplateItem: React.FC<TemplateItemProps> = ({ template }) => {
  const { memberId } = useParams();

  const { data: pqAttributesData } = useQuery(
    PerformanceQualityAttributesDocument,
  );
  const pqAttributes = pqAttributesData?.performanceQualityAttributes;

  return (
    <TableRow>
      <TableCell $paddingOverride="10px">
        <Link
          to={`/app/members/${memberId}/gameplan/?duplicate=${template.id}&navigationDepth=2`}
          trackingData={{
            eventName: EventNames.LinkClick,
            event_label: EventLabels.CustomPlanTemplateSelect,
            training_plan_template_id: template.id,
            event_category: EventCategories.CustomPlan,
            member_id: memberId,
          }}
        >
          <Text kind={TextKind.ButtonM}>{template.name}</Text>
        </Link>
      </TableCell>

      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>{startCase(template.focusArea ?? '')}</Text>
      </TableCell>
      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>
          {template.targetedPerformanceQualityAttributes
            .map(
              (pq) =>
                pqAttributes?.find(
                  (pqAttribute) => pqAttribute.value === pq.value,
                )?.name,
            )
            .filter(Boolean)
            .join(', ')}
        </Text>
      </TableCell>
      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>{template.commitmentLevel}</Text>
      </TableCell>
      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>{getPhase(template.phase)}</Text>
      </TableCell>
      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>
          {getTrainingModeText(template.trainingMode)}
        </Text>
      </TableCell>
    </TableRow>
  );
};

const PAGE_SIZE = 100;

interface GameplanTemplatesProps {}

export const GameplanTemplates: React.FC<GameplanTemplatesProps> = () => {
  const { flags } = useFeatureFlags();
  const enableE3 = flags[SplitFlags.CH_E3];

  const navigate = useNavigate();
  const { memberId } = useParams();
  const { data: memberData } = useQuery(MemberDocument, {
    variables: { id: memberId! },
    skip: !memberId,
  });
  const memberIsHpec = memberData?.user?.hasHighPerformanceCoaching;

  const [filter, setFilter] = useState<CustomPlanTemplatesFilter>({});
  const {
    data: customPlanTemplatesData,
    previousData: customPlanTemplatesPreviousData,
    fetchMore: fetchMoreCustomPlanTemplates,
    networkStatus: customPlanTemplatesNetworkStatus,
    error,
  } = useQuery(CustomPlanTemplatesDocument, {
    variables: {
      input: {
        first: PAGE_SIZE,
      },
      filter,
    },
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const isFetchingMoreTemplates =
    customPlanTemplatesNetworkStatus === NetworkStatus.fetchMore;
  const isRefetchingTemplates = [
    NetworkStatus.refetch,
    NetworkStatus.setVariables,
  ].includes(customPlanTemplatesNetworkStatus);
  const isLoadingTemplates =
    customPlanTemplatesNetworkStatus === NetworkStatus.loading;
  const templatesPageInfo =
    customPlanTemplatesData?.customPlanTemplates?.pageInfo;
  const hasNextPage = templatesPageInfo?.hasNextPage;
  const endCursor = templatesPageInfo?.endCursor;

  const fetchMoreTemplates = async () => {
    if (!hasNextPage || !endCursor || isFetchingMoreTemplates) {
      return;
    }
    return await fetchMoreCustomPlanTemplates({
      variables: {
        input: {
          first: PAGE_SIZE,
          after: endCursor,
        },
      },
    });
  };

  if (!enableE3) {
    return null;
  }

  const templates =
    customPlanTemplatesData?.customPlanTemplates.edges ||
    customPlanTemplatesPreviousData?.customPlanTemplates.edges ||
    [];

  const trackingData = {
    event_category: EventCategories.CustomPlan,
    member_id: memberId,
  };

  return (
    <StyledPage>
      <FeatureHeader
        state="create"
        name="gameplan"
        trackingData={{
          event_category: EventCategories.CustomPlan,
        }}
      />
      <StyledMain>
        <TitleWrapper>
          <Text kind={TextKind.TitleL}>Select a gameplan template</Text>
          {memberIsHpec && (
            <Button
              onClick={() =>
                navigate(`/app/members/${memberId}/gameplan?navigationDepth=2`)
              }
              trackingData={{
                ...trackingData,
                event_label: EventLabels.CustomPlanTemplateSkip,
              }}
            >
              Start from scratch
            </Button>
          )}
        </TitleWrapper>

        <GameplanTemplateFilters
          trackingData={trackingData}
          setFilter={setFilter}
        />

        <TableContainer
          style={{
            maxHeight: '70vh',
            overflowY: 'scroll',
          }}
        >
          {isLoadingTemplates && <LoadingPanel noBorder height={300} />}
          {isRefetchingTemplates && <LoadingPanel overlay noBorder />}
          {error && (
            <GenericError testId="customPlanTemplates-error" error={error} />
          )}
          {!error && !isLoadingTemplates && templates?.length === 0 && (
            <StyledEmptyTable>
              <Text kind={TextKind.TitleXs}>No templates found.</Text>
            </StyledEmptyTable>
          )}
          {templates?.length > 0 && (
            <Table>
              <colgroup>
                <col width="auto" />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
                <col width="15%" />
              </colgroup>
              <TableHead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <TableRow>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Gameplan
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Focus Area
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Performance Quality
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Commitment
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Phase
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Training Mode
                    </Text>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map(({ node: template }) => (
                  <TemplateItem key={template.id} template={template} />
                ))}
              </TableBody>
            </Table>
          )}

          {hasNextPage && (
            <div style={{ padding: '1rem 0' }}>
              <PaginationWaypoint callback={fetchMoreTemplates} />
              <LoadMore
                loadMore={fetchMoreTemplates}
                isFetchingMore={isFetchingMoreTemplates}
              />
            </div>
          )}
        </TableContainer>
      </StyledMain>
    </StyledPage>
  );
};
